import type { StoryCtx } from './ctx.js';
import type { NodeKind, StoryNode } from './types.js';

/** Welche Arten von Nodes unter einem Elternnode liegen dürfen. */
export const ALLOWED_CHILDREN: Record<NodeKind, NodeKind[]> = {
  // Szenen (Zeitpunkt `event`, Zeitraum `period`) liegen lose auf einer Fläche, angedockt an einem Zeitstrahl oder – nicht auf der
  // Fläche – unter dem Manuskript; in ihrem Inneren liegt ihre Besetzung (Personen, Orte, Dinge) und Notizen
  story: ['timeline', 'instance', 'normal', 'event', 'period', 'manuscript'],
  timeline: ['event', 'period'],
  event: ['instance', 'normal'],
  period: ['instance', 'normal'],
  instance: ['instance', 'normal', 'event', 'period'],
  normal: ['instance', 'normal', 'event', 'period'],
  // Gliederung: Kapitel liegen im Manuskript oder in einem Abschnitt darin; welche Szenen in einem Kapitel stehen, sagt deren `chapterId`
  manuscript: ['section', 'chapter', 'event', 'period'],
  section: ['chapter'],
  chapter: [],
  scene: [],
};

export const childrenOf = (ctx: StoryCtx, id: string): StoryNode[] => ctx.children.get(id) ?? [];

/** Pfad von der Story bis zum Node (Wurzel zuerst). Grundlage der Brotkrumenleiste. */
export function pathTo(ctx: StoryCtx, id: string): StoryNode[] {
  const path: StoryNode[] = [];
  const seen = new Set<string>();
  let cur = ctx.nodes.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    path.unshift(cur);
    cur = cur.parentId ? ctx.nodes.get(cur.parentId) : undefined;
  }
  return path;
}

/** Alle Nachfahren (ohne den Node selbst). */
export function descendants(ctx: StoryCtx, id: string): StoryNode[] {
  const out: StoryNode[] = [];
  const stack = [...childrenOf(ctx, id)];
  while (stack.length > 0) {
    const n = stack.pop()!;
    out.push(n);
    stack.push(...childrenOf(ctx, n.id));
  }
  return out;
}

/** Ob man per Doppelklick in den Node eintauchen kann (innerer Canvas). Der Zeitstrahl selbst hat keinen. */
export const canEnter = (n: StoryNode): boolean => n.kind !== 'timeline' && n.kind !== 'manuscript' && n.kind !== 'section' && n.kind !== 'chapter' && n.kind !== 'scene';

/** Nodes, die auf dem Canvas von `currentId` erscheinen: ihre Kinder (Zeitstrahle, lose Szenen, freie Nodes) plus die Szenen an ihren Zeitstrahlen. */
export function visibleOn(ctx: StoryCtx, currentId: string): StoryNode[] {
  const current = ctx.nodes.get(currentId);
  if (!current) return [];
  // das Manuskript (Erzählebene) liegt unter der Geschichte, erscheint aber nie auf der Fläche
  const direct = childrenOf(ctx, currentId).filter((n) => n.kind !== 'manuscript');
  // die Szenen an den Zeitstrahlen dieses Canvas
  const items = direct.filter((n) => n.kind === 'timeline').flatMap((t) => childrenOf(ctx, t.id));
  return [...direct, ...items];
}
